import { isColorblindFriendly } from './useAccessibilitySettings';

type RgbTriplet = readonly [number, number, number];

interface TimingBucket {
  maxHours: number;
  rgb: RgbTriplet;
  boundaryLabel: string;
}

export const TIMING_BUCKETS: readonly TimingBucket[] = [
  { maxHours: 0.5, rgb: [0, 153, 68] as const, boundaryLabel: '30m' },
  { maxHours: 1, rgb: [0, 200, 83] as const, boundaryLabel: '1h' },
  { maxHours: 3, rgb: [255, 235, 59] as const, boundaryLabel: '3h' },
  { maxHours: 6, rgb: [255, 193, 7] as const, boundaryLabel: '6h' },
  { maxHours: 12, rgb: [255, 152, 0] as const, boundaryLabel: '12h' },
  { maxHours: 24, rgb: [244, 67, 54] as const, boundaryLabel: '24h' },
  { maxHours: 48, rgb: [136, 14, 79] as const, boundaryLabel: '48h' },
] as const;

// A blue-to-yellow sequential scale based on the Cividis family. It preserves
// lightness ordering and avoids relying on red/green differences.
export const COLORBLIND_TIMING_BUCKETS: readonly TimingBucket[] = [
  { maxHours: 0.5, rgb: [59, 73, 112] as const, boundaryLabel: '30m' },
  { maxHours: 1, rgb: [82, 101, 133] as const, boundaryLabel: '1h' },
  { maxHours: 3, rgb: [111, 130, 151] as const, boundaryLabel: '3h' },
  { maxHours: 6, rgb: [143, 161, 166] as const, boundaryLabel: '6h' },
  { maxHours: 12, rgb: [174, 184, 157] as const, boundaryLabel: '12h' },
  { maxHours: 24, rgb: [211, 198, 119] as const, boundaryLabel: '24h' },
  { maxHours: 48, rgb: [249, 231, 33] as const, boundaryLabel: '48h' },
] as const;

const UNAVAILABLE_RGB = [120, 120, 120] as const;
const ORDER_QUANTIZATION_PER_HOUR = 12; // 5-minute steps

export const TIMING_WINDOW_HOURS = TIMING_BUCKETS[TIMING_BUCKETS.length - 1].maxHours;
export const TIMING_BOUNDARY_TICKS = ['0', ...TIMING_BUCKETS.map((bucket) => bucket.boundaryLabel)] as const;
export const NO_UPCOMING_HOURS = Number.POSITIVE_INFINITY;

export function getActiveTimingBuckets(): readonly TimingBucket[] {
  return isColorblindFriendly.value ? COLORBLIND_TIMING_BUCKETS : TIMING_BUCKETS;
}

function toRgba(rgb: RgbTriplet, alpha: number): string {
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

function normalizeHours(hours: number): number {
  if (!Number.isFinite(hours)) return NO_UPCOMING_HOURS;
  return Math.max(0, hours);
}

function isUnavailableNormalized(normalizedHours: number): boolean {
  return !Number.isFinite(normalizedHours);
}

export function isTimingUnavailable(hours: number): boolean {
  return isUnavailableNormalized(normalizeHours(hours));
}

/**
 * Return the timing-bucket index used by the shared color scale.
 * Finite values beyond the final boundary stay in the final color bucket.
 * TIMING_BUCKETS.length is reserved for an unavailable/no-pass value.
 */
export function getTimingBucketIndexForHours(hours: number): number {
  const normalized = normalizeHours(hours);
  if (isUnavailableNormalized(normalized)) return TIMING_BUCKETS.length;
  for (let i = 0; i < TIMING_BUCKETS.length; i++) {
    if (normalized <= TIMING_BUCKETS[i].maxHours) return i;
  }
  return TIMING_BUCKETS.length - 1;
}

/**
 * Return fill color for "hours to next pass" using the same thresholds as the hex legend.
 */
export function getTimingColorForHours(
  hours: number,
  alpha = 1,
  unavailableAlpha = 0.6,
): string {
  const normalized = normalizeHours(hours);
  if (isUnavailableNormalized(normalized)) {
    return toRgba(UNAVAILABLE_RGB, unavailableAlpha);
  }
  for (const bucket of getActiveTimingBuckets()) {
    if (normalized <= bucket.maxHours) {
      return toRgba(bucket.rgb, alpha);
    }
  }
  const buckets = getActiveTimingBuckets();
  return toRgba(buckets[buckets.length - 1].rgb, alpha);
}

/**
 * Larger rank = sooner pass. Intended for style z-index ordering.
 */
export function getTimingOrderRank(hours: number): number {
  const normalized = Math.min(TIMING_WINDOW_HOURS, normalizeHours(hours));
  return Math.max(0, Math.round((TIMING_WINDOW_HOURS - normalized) * ORDER_QUANTIZATION_PER_HOUR));
}
